import { MutableRefObject, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import Button from '@components/button/Button';
import Icon, { IconSize } from '@components/icon/Icon';
import useOnClickOutside from '@utils/hooks';

interface ConfirmationModalV2Props {
  title?: string;
  description?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  iconName?: string;
  iconSize?: IconSize;
  confirmButtonLabel?: string;
  isConfirmButtonDisabled?: boolean;
  isConfirmButtonLoading?: boolean;
  confirmButtonStyles?: string;
  onConfirm: () => void;
  cancelButtonLabel?: string;
  onCancel: () => void;
  isClickOutsideRequired?: boolean;
  children?: React.ReactNode;
  modalClassName?: string;
  usePortal?: boolean;
}

const ConfirmationModalV2 = ({
  title,
  description,
  className = '',
  contentClassName = '',
  iconName,
  iconSize = '48',
  onConfirm,
  onCancel,
  confirmButtonLabel = 'Confirm',
  isConfirmButtonDisabled = false,
  isConfirmButtonLoading = false,
  confirmButtonStyles = '',
  cancelButtonLabel = 'Cancel',
  isClickOutsideRequired = true,
  children,
  modalClassName = '',
  usePortal = true
}: ConfirmationModalV2Props) => {
  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;

  useOnClickOutside(modalRef, (e) => {
    if (isClickOutsideRequired && onCancel) {
      e.stopPropagation();
      onCancel();
    }
  });

  const modalUi = (
    <div
      className={`fixed left-0 top-0 z-[60] flex size-full items-center justify-center ${modalClassName}`}
    >
      {/* Modal Overlay */}
      <div className="absolute size-full bg-black opacity-30" />

      {/* Modal Main Content */}
      <div
        className={`relative mx-auto flex max-h-[98%] w-120 flex-col overflow-hidden rounded-lg bg-cofeeNeutral1 ${className}`}
        ref={modalRef}
      >
        <div
          className={`flex flex-col items-center justify-center gap-8 p-6 ${contentClassName}`}
        >
          {iconName && (
            <div className="flex items-center justify-center">
              <Icon name={iconName} size={iconSize} />
            </div>
          )}
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            {title && (
              <span className="text-lg font-semibold text-cofeeNeutral10">
                {title}
              </span>
            )}
            {description && (
              <div className="whitespace-pre-line text-base font-normal text-cofeeNeutral7">
                {description}
              </div>
            )}
          </div>
          {children}
          <div className="flex w-full items-center justify-center gap-2">
            <Button
              variant="tertiary"
              handleClick={onCancel}
              className="w-full"
            >
              {cancelButtonLabel}
            </Button>
            <Button
              variant="primary"
              handleClick={onConfirm}
              className={clsx(
                'w-full !text-cofeeNeutral1',
                confirmButtonStyles
              )}
              disabled={isConfirmButtonDisabled}
              loading={isConfirmButtonLoading}
            >
              {confirmButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return usePortal ? createPortal(modalUi, document.body) : modalUi;
};

export default ConfirmationModalV2;
